// src/features/records/components/RecordDetail/components/RecordAttributes.js
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  CircleCheckBig, 
  MapPin, 
  Flag,
  Globe
} from 'lucide-react';
import { sanitizeText } from '../../../../../utils/sanitize';
import { formatDueDate as formatTaskDueDate } from '../../TaskView/utils/taskDateUtils';
import { buildDateKey, coerceDateValue, resolveTimeZone } from '../../../../../utils/dateUtils';
import styles from '../RecordDetail.module.css';

const RecordAttributes = ({ attributes }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;

  if (!attributes || Object.keys(attributes).length === 0) {
    return null;
  }

  const { dueDate, dueDateSemantics, priority, date, location } = attributes;
  const dueDateTimeZone =
    typeof dueDateSemantics?.timezone === 'string'
      ? dueDateSemantics.timezone
      : undefined;
  const viewerTimeZone = resolveTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Helper to format calendar date range
  const formatCalendarDate = (dateRange) => {
    if (!dateRange || !dateRange.start) return null;

    const startDate = coerceDateValue(dateRange.start);
    if (!startDate) {
      return null;
    }

    const endDate = dateRange.end
      ? coerceDateValue(dateRange.end) || startDate
      : startDate;
    const timezone = resolveTimeZone(dateRange.timezone) || viewerTimeZone;
    const isAllDay = dateRange.isAllDay === true;
    const allDayEnd =
      isAllDay && endDate.getTime() > startDate.getTime()
        ? new Date(endDate.getTime() - 1)
        : endDate;
    const keyOptions = { locale, timeZone: timezone };
    const formatOptionsWithZone = (options) => ({
      ...options,
      ...(timezone ? { timeZone: timezone } : {}),
    });
    const longDateFormatter = new Intl.DateTimeFormat(locale, formatOptionsWithZone({
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }));
    const shortDateFormatter = new Intl.DateTimeFormat(locale, formatOptionsWithZone({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }));
    const timeFormatter = new Intl.DateTimeFormat(locale, formatOptionsWithZone({
      hour: 'numeric',
      minute: '2-digit',
    }));
    const dateTimeFormatter = new Intl.DateTimeFormat(locale, formatOptionsWithZone({
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }));
    const isSameDay =
      buildDateKey(startDate, keyOptions) === buildDateKey(isAllDay ? allDayEnd : endDate, keyOptions);

    // Format based on whether it's all-day or has specific times
    if (isAllDay) {
      if (isSameDay) {
        return {
          primary: longDateFormatter.format(startDate),
          secondary: t('records.time.all_day', 'All day'),
          timezone: timezone && timezone !== viewerTimeZone ? timezone : null
        };
      } else {
        return {
          primary: `${shortDateFormatter.format(startDate)} - ${shortDateFormatter.format(allDayEnd)}`,
          secondary: t('records.time.all_day', 'All day'),
          timezone: timezone && timezone !== viewerTimeZone ? timezone : null
        };
      }
    } else {
      if (isSameDay) {
        return {
          primary: longDateFormatter.format(startDate),
          secondary: `${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)}`,
          timezone: timezone && timezone !== viewerTimeZone ? timezone : null
        };
      } else {
        return {
          primary: `${dateTimeFormatter.format(startDate)} - ${dateTimeFormatter.format(endDate)}`,
          secondary: null,
          timezone: timezone && timezone !== viewerTimeZone ? timezone : null
        };
      }
    }
  };

  // Helper to render priority
  const renderPriority = (priority) => {
    const priorityConfig = {
      high: {
        className: styles.priorityHigh,
        label: t('records.detail.attributes.priority.high', 'High priority')
      },
      medium: {
        className: styles.priorityMedium,
        label: t('records.detail.attributes.priority.medium', 'Medium priority')
      },
      low: {
        className: styles.priorityLow,
        label: t('records.detail.attributes.priority.low', 'Low priority')
      }
    };

    const config = priorityConfig[priority];
    if (!config) return null;

    return (
      <div className={`${styles.priorityBadge} ${config.className}`}>
        <Flag size={12} />
        <span>{config.label}</span>
      </div>
    );
  };

  // Check if we have any attributes to display
  const hasAttributes = dueDate || priority || date || location;
  if (!hasAttributes) {
    return null;
  }

  return (
    <div className={styles.attributes}>
      {/* Task Attributes */}
      {(dueDate || priority) && (
        <div className={styles.attributeCard}>
          <div className={styles.attributeIcon}>
            <CircleCheckBig size={20} />
          </div>
          <div className={styles.attributeContent}>
            <h4 className={styles.attributeLabel}>
              {t('records.detail.attributes.task_title', 'Task')}
            </h4>
            {dueDate && (
              <div className={styles.attributeValue}>
                <span className={styles.dueDate}>
                  {/* Detail view should always show the actual due date.
                      Completion status text requires a dedicated completion timestamp. */}
                  {formatTaskDueDate(dueDate, false, {
                    locale,
                    timeZone: dueDateTimeZone,
                    t,
                  })}
                </span>
              </div>
            )}
            {priority && (
              <div className={styles.attributeValue}>
                {renderPriority(priority)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Calendar Attributes */}
      {date && (
        <div className={styles.attributeCard}>
          <div className={styles.attributeIcon}>
            <Calendar size={20} />
          </div>
          <div className={styles.attributeContent}>
            <h4 className={styles.attributeLabel}>
              {t('records.detail.attributes.event_title', 'Event')}
            </h4>
            {(() => {
              const formatted = formatCalendarDate(date);
              if (!formatted) return null;
              
              return (
                <>
                  <div className={styles.attributeValue}>
                    <span className={styles.eventDate}>{formatted.primary}</span>
                  </div>
                  {formatted.secondary && (
                    <div className={styles.attributeSubValue}>
                      <Calendar size={14} />
                      <span>{formatted.secondary}</span>
                    </div>
                  )}
                  {formatted.timezone && (
                    <div className={styles.attributeSubValue}>
                      <Globe size={14} />
                      <span className={styles.timezone}>{formatted.timezone}</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Location Attributes */}
      {location && (
        <div className={styles.attributeCard}>
          <div className={styles.attributeIcon}>
            <MapPin size={20} />
          </div>
          <div className={styles.attributeContent}>
            <h4 className={styles.attributeLabel}>
              {t('records.detail.attributes.location_title', 'Location')}
            </h4>
            <div className={styles.attributeValue}>
              <span className={styles.locationName}>{sanitizeText(location?.name || '')}</span>
            </div>
            <div className={styles.attributeAction}>
              <button className={styles.viewMapButton} type="button">
                {t('records.detail.attributes.view_map', 'View map')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RecordAttributes.propTypes = {
  attributes: PropTypes.shape({
    dueDate: PropTypes.any, // Firestore Timestamp
    dueDateSemantics: PropTypes.shape({
      precision: PropTypes.oneOf(['date', 'datetime']),
      timezone: PropTypes.string,
      localDate: PropTypes.string,
    }),
    isComplete: PropTypes.bool,
    priority: PropTypes.oneOf(['high', 'medium', 'low']),
    date: PropTypes.shape({
      start: PropTypes.any, // Firestore Timestamp
      end: PropTypes.any, // Firestore Timestamp
      timezone: PropTypes.string,
      isAllDay: PropTypes.bool
    }),
    location: PropTypes.shape({
      name: PropTypes.string.isRequired,
      lat: PropTypes.number,
      lng: PropTypes.number,
      geohash: PropTypes.string,
      source: PropTypes.oneOf(['google', 'mapbox', 'manual']),
      context: PropTypes.string,
      placeId: PropTypes.string
    })
  })
};

export default RecordAttributes;
